import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";


import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/components/auth/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import MacEasterEgg from "@/components/MacEasterEgg";

// Public Pages
import Home from "@/pages/Home";
import About from "@/pages/About";
import Games from "@/pages/Games";
import Training from "@/pages/Training";
import Join from "@/pages/Join";
import Donate from "@/pages/Donate";
import Veterans from "@/pages/Veterans";
import MilsimRegistry from "@/pages/MilsimRegistry";
import MilsimGroup from "@/pages/MilsimGroup";
import MilsimRegister from "@/pages/MilsimRegister";
import MilsimManage from "@/pages/portal/MilsimManage";
import MemberHQ from "@/pages/portal/MemberHQ";
import NotFound from "@/pages/not-found";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import OpsCalendar from "@/pages/OpsCalendar";
import Forum from "@/pages/Forum";
import UserPublicProfile from "@/pages/UserPublicProfile";

// New Pages
import ServiceCard from "@/pages/portal/ServiceCard";
import RibbonRack from "@/pages/portal/RibbonRack";
import Stats from "@/pages/Stats";

// Portal Pages
import Login from "@/pages/portal/Login";
import Register from "@/pages/portal/Register";
import Dashboard from "@/pages/portal/Dashboard";
import Inbox from "@/pages/portal/Inbox";
import Compose from "@/pages/portal/Compose";
import Comms from "@/pages/portal/Comms";
import Apply from "@/pages/portal/Apply";
import ModPanel from "@/pages/portal/ModPanel";
import AdminPanel from "@/pages/portal/AdminPanel";
import CommandCenter from "@/pages/portal/CommandCenter";
import Friends from "@/pages/portal/Friends";
import SecurityProtocol from "@/pages/portal/SecurityProtocol";
import Profile from "@/pages/portal/Profile";
import ResetPassword from "@/pages/portal/ResetPassword";
import VerifyEmail from "@/pages/portal/VerifyEmail";
import TwoFactorAuth from "@/pages/portal/TwoFactorAuth";
import Support from "@/pages/portal/Support";
import SupportAdmin from "@/pages/portal/SupportAdmin";
import CommanderPro from "@/pages/CommanderPro";
import JointOps from "@/pages/JointOps";
import RecruitmentMarketplace from "@/pages/RecruitmentMarketplace";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data considered fresh for 15 minutes — no refetch within that window
      staleTime: 15 * 60 * 1000,
      // Keep unused data in cache for 30 minutes after last use
      gcTime: 30 * 60 * 1000,
      // Don't refetch just because the user switches tabs / windows
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect unless stale
      refetchOnReconnect: "always",
      // Retry failed requests once before surfacing the error
      retry: 1,
      retryDelay: 2000,
    },
  },
});


function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/about" component={About} />
      <Route path="/games" component={Games} />
      <Route path="/training" component={Training} />
      <Route path="/join" component={Join} />
      <Route path="/donate" component={Donate} />
      <Route path="/veterans" component={Veterans} />
      <Route path="/milsim" component={MilsimRegistry} />
      <Route path="/milsim/register" component={MilsimRegister} />
      <Route path="/milsim/:slug" component={MilsimGroup} />
      <Route path="/portal/milsim" component={MilsimManage} />
      <Route path="/portal/member-hq" component={MemberHQ} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/ops" component={OpsCalendar} />
      <Route path="/forum" component={Forum} />
      <Route path="/u/:username" component={UserPublicProfile} />
      
      {/* Portal Routes */}
      <Route path="/portal/login" component={Login} />
      <Route path="/portal/register" component={Register} />
      <Route path="/portal/dashboard" component={Dashboard} />
      <Route path="/portal/inbox" component={Inbox} />
      <Route path="/portal/compose" component={Compose} />
      <Route path="/portal/comms" component={Comms} />
      <Route path="/portal/apply" component={Apply} />
      <Route path="/portal/profile" component={Profile} />
      <Route path="/portal/mod" component={ModPanel} />
      <Route path="/portal/admin" component={AdminPanel} />
      <Route path="/portal/command" component={CommandCenter} />
      <Route path="/portal/friends" component={Friends} />
      <Route path="/portal/security" component={SecurityProtocol} />
      <Route path="/portal/reset-password" component={ResetPassword} />
      <Route path="/portal/verify-email" component={VerifyEmail} />
      <Route path="/portal/2fa" component={TwoFactorAuth} />
      <Route path="/portal/service-card" component={ServiceCard} />
      <Route path="/portal/ribbon-rack" component={RibbonRack} />
      <Route path="/portal/support" component={Support} />
      <Route path="/portal/support-admin" component={SupportAdmin} />
      <Route path="/commander-pro" component={CommanderPro} />
      <Route path="/stats" component={Stats} />
      <Route path="/joint-ops" component={JointOps} />
      <Route path="/recruitment" component={RecruitmentMarketplace} />
      <Route path="/leaderboard" component={JointOps} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <WouterRouter>
            <Router />
          </WouterRouter>
          <Toaster />
          <MacEasterEgg />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
